import React, { useState, useEffect, useCallback, useRef } from 'react';
import withAuth from '../../../HOC/withAuth';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import WatermarkEditor from '../../../components/WatermarkEditor';
import PreviewResolutionPanel from '../../../components/PreviewResolutionPanel';
import { normalizeWatermarkEditorConfig } from '../../../watermarkPreviewPolicy';
import StepShell from '../StepShell';
import C from '../../../colors';
import { hasOrderSelections } from '../../../orderStatus';

const TAG_COLORS = ['#F777B0', '#5B8CF5', '#52B788', '#F4A261'];

function TagPanel({ tags, availableTags, onSave, onCreate }) {
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);

  const add = async () => {
    const v = input.trim();
    if (!v || tags.some(tag => tag.name === v) || tags.length >= 4 || saving) return;
    const existing = availableTags.find(tag => tag.name === v);
    setSaving(true);
    try {
      if (existing) await onSave([...tags, existing]);
      else await onCreate(v, TAG_COLORS[availableTags.length % TAG_COLORS.length]);
      setInput('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '32px 36px' }}>
      <div style={{ maxWidth: 540 }}>
        <div style={{ fontSize: 11, letterSpacing: 1.4, textTransform: 'uppercase', fontWeight: 600, color: C.brand, marginBottom: 6 }}>
          客户标签
        </div>
        <div style={{ fontFamily: C.serif, fontSize: 26, color: C.ink, marginBottom: 8 }}>照片标记</div>
        <div style={{ fontSize: 13, color: C.ink3, lineHeight: 1.6, marginBottom: 28 }}>
          为这个订单设置最多 4 个标签。客户在选片时可以给每张照片打上这些标签，
          比如"需要修图"、"装订成册"，方便你后期处理。
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 24 }}>
          {tags.map((tag, i) => (
            <span key={tag.id} data-testid={`order-photo-tag-${i}`} data-tag-id={tag.id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '8px 14px', borderRadius: 24,
              background: `${tag.color || TAG_COLORS[i]}18`, border: `1.5px solid ${tag.color || TAG_COLORS[i]}`,
              color: tag.color || TAG_COLORS[i], fontSize: 14, fontWeight: 500,
            }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: tag.color || TAG_COLORS[i], flexShrink: 0 }} />
              {tag.name}
              <button
                data-testid={`order-photo-tag-remove-${i}`}
                onClick={() => onSave(tags.filter(t => t.id !== tag.id))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: tag.color || TAG_COLORS[i], fontSize: 16, lineHeight: 1, padding: 0, opacity: 0.7 }}>
                ×
              </button>
            </span>
          ))}
        </div>

        {tags.length < 4 ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              data-testid="order-photo-tag-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && add()}
              placeholder={`标签 ${tags.length + 1}，回车添加`}
              style={{
                flex: 1, height: 42, padding: '0 16px', borderRadius: 10,
                border: `1.5px solid ${C.hair2}`, background: '#fff',
                fontFamily: C.sans, fontSize: 14, color: C.ink, outline: 'none',
                transition: 'border-color .2s',
              }}
              onFocus={e => { e.target.style.borderColor = C.brand; }}
              onBlur={e => { e.target.style.borderColor = C.hair2; }}
            />
            <button data-testid="order-photo-tag-add" disabled={saving} onClick={add} style={{
              height: 42, padding: '0 18px', borderRadius: 10, border: 'none',
              background: C.ink, color: '#fff', cursor: 'pointer',
              fontFamily: C.sans, fontSize: 14, fontWeight: 500,
            }}>{saving ? '保存中…' : '添加'}</button>
          </div>
        ) : (
          <div data-testid="order-photo-tag-limit" style={{ fontSize: 12, color: C.ink3 }}>已达到 4 个标签上限</div>
        )}

        {tags.length < 4 && availableTags.some(tag => !tags.some(selected => selected.id === tag.id)) && (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 11, color: C.ink3, marginBottom: 8 }}>从我的标签库中选择</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {availableTags.filter(tag => !tags.some(selected => selected.id === tag.id)).map(tag => (
                <button key={tag.id} data-testid={`order-photo-tag-option-${tag.id}`} onClick={() => onSave([...tags, tag])} style={{
                  padding: '6px 10px', borderRadius: 16, border: `1px solid ${tag.color || C.hair2}`,
                  background: '#fff', color: tag.color || C.ink2, fontFamily: C.sans, fontSize: 12, cursor: 'pointer',
                }}>{tag.name}</button>
              ))}
            </div>
          </div>
        )}

        {tags.length === 0 && (
          <div style={{
            marginTop: 28, padding: '16px 18px', borderRadius: 10,
            background: C.paper2, border: `1px solid ${C.hair}`,
            fontSize: 13, color: C.ink3, lineHeight: 1.6,
          }}>
            暂未设置标签。如不需要客户标记照片，留空即可。
          </div>
        )}
      </div>
    </div>
  );
}

function TabIcon({ name }) {
  const common = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (name === 'watermark') {
    return <svg {...common}><path d="M12 3 4 6v6c0 4.5 3.4 8.3 8 9 4.6-.7 8-4.5 8-9V6l-8-3Z"/><path d="m9 12 2 2 4-4"/></svg>;
  }
  if (name === 'resolution') {
    return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M7 15V9l3 3 3-3v6"/><path d="M16 9v6"/></svg>;
  }
  return <svg {...common}><path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8Z"/><circle cx="7.5" cy="7.5" r="1.5"/></svg>;
}

const Step2 = inject('snackbarStore', 'ordersStore', 'userStore', 'chargeStore')(observer(({ snackbarStore, ordersStore, userStore, chargeStore }) => {
  const { orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState('watermark');
  const [tags, setTags] = useState([]);
  const [wm, setWm] = useState({
    enabled: false, text: '', style: 'single', pos: 'bottom-right',
    size: 36, alpha: 0.5, shadow: false, inset: 24, rotate: 0, weight: 400,
  });
  const wmRef = useRef(wm);
  const repairedLegacyShadowRef = useRef(new Set());
  const order = ordersStore.currentOrder || {};
  const hasSelections = hasOrderSelections(order);
  const availableTags = userStore.globalTags;
  const globalTagsLoaded = userStore.globalTagsLoaded;
  const setWmSync = useCallback((newWm) => {
    wmRef.current = newWm;
    setWm(newWm);
  }, []);

  useEffect(() => {
    if (!orderId) return;
    ordersStore.fetchOrder(orderId);
    userStore.fetchTags().catch(() => {});
  }, [orderId]);

  useEffect(() => {
    const o = ordersStore.currentOrder;
    if (!o) return;
    const activeTagIds = new Set(availableTags.map(tag => String(tag.id)));
    setTags((o.tagDefinitions || []).filter(tag =>
      !globalTagsLoaded || activeTagIds.has(String(tag.id))
    ));
    const nextWm = normalizeWatermarkEditorConfig({
      enabled: o.openWatermark || false,
      text: o.watermark || '',
      style: o.watermarkStyle || 'single',
      pos: o.watermarkPosition || 'bottom-right',
      size: o.watermarkFontSize || 36,
      alpha: o.watermarkAlpha || 0.5,
      shadow: o.watermarkShadow ?? false,
      inset: o.watermarkInset ?? 24,
      rotate: o.watermarkRotate != null
        ? o.watermarkRotate
        : 0,
      weight: 400,
    });
    setWmSync(nextWm);

    const hasLegacyShadowConflict = Number(nextWm.rotate) !== 0 && Boolean(o.watermarkShadow);
    const repairKey = `${o.id || orderId}:${nextWm.rotate}`;
    if (hasLegacyShadowConflict && String(o.id) === String(orderId) && !repairedLegacyShadowRef.current.has(repairKey)) {
      repairedLegacyShadowRef.current.add(repairKey);
      ordersStore.updateOrder(orderId, { watermarkShadow: false }).catch(() => {
        repairedLegacyShadowRef.current.delete(repairKey);
        snackbarStore.showSnackbar('旧水印投影配置修正失败，请重试', 'error');
      });
    }
  }, [ordersStore.currentOrder, availableTags, globalTagsLoaded, orderId, ordersStore, snackbarStore, setWmSync]);

  const handleWmSave = useCallback((nextWm) => {
    const current = nextWm && typeof nextWm === 'object' && 'enabled' in nextWm
      ? nextWm
      : wmRef.current;
    ordersStore.saveWatermark(orderId, current)
      .then(() => snackbarStore.showSnackbar('水印设置已保存'))
      .catch(() => snackbarStore.showSnackbar('保存失败，请重试', 'error'));
  }, [orderId, ordersStore, snackbarStore]);

  const handleTagsSave = useCallback(async (newTags) => {
    const previous = tags;
    setTags(newTags);
    try {
      await ordersStore.saveOrderTags(orderId, newTags.map(tag => tag.id));
      snackbarStore.showSnackbar('标签已保存');
    } catch (error) {
      setTags(previous);
      snackbarStore.showSnackbar('标签保存失败', 'error');
      throw error;
    }
  }, [orderId, ordersStore, snackbarStore, tags]);

  const handleTagCreate = useCallback(async (name, color) => {
    try {
      const created = await userStore.createTag({ name, color, kind: 'workflow' });
      await handleTagsSave([...tags, created]);
    } catch (error) {
      snackbarStore.showSnackbar(error?.response?.data?.error || '标签创建失败', 'error');
      throw error;
    }
  }, [handleTagsSave, snackbarStore, tags, userStore]);

  const resolutionNames = { 1080: '标清', 1920: '高清', 2560: '超清' };
  const orderResolution = ordersStore.currentOrder?.previewMaxDimension
    || ordersStore.currentOrder?.effectivePreviewMaxDimension;
  const tabs = [
    {
      id: 'watermark', label: '水印保护',
      status: wm.enabled ? `已开启 · ${wm.style === 'single' ? '单个' : '满铺'}` : '未开启',
      warn: !wm.enabled,
    },
    {
      id: 'resolution', label: '清晰度',
      status: orderResolution ? `${resolutionNames[orderResolution] || ''} · 长边 ${orderResolution}` : '—',
    },
    {
      id: 'tags', label: '照片标记',
      status: tags.length > 0 ? `${tags.length} 个标记` : '未设置',
    },
  ];
  const globalWatermarkEnabled = userStore.openWatermark === true
    || userStore.openWatermark === 1
    || userStore.openWatermark === '1';
  const globalWatermarkConfigured = globalWatermarkEnabled && userStore.isWatermarkSet();
  const showGlobalSettingHint = tab === 'watermark'
    ? userStore.userInfoLoaded && !globalWatermarkConfigured
    : tab === 'tags' && globalTagsLoaded && availableTags.length === 0;

  return (
    <StepShell order={order}>
      {location.state?.watermarkReview && (
        <div data-testid="watermark-review-guide" style={{
          padding: '14px 36px',
          background: C.brandSoft, color: C.ink2,
          borderBottom: `1px solid ${C.hair}`,
          fontSize: 13, lineHeight: 1.6,
        }}>
          <strong style={{ color: C.brand2 }}>上传前确认一下水印。</strong>
          你的全局水印尚未启用。可以在这里为本订单开启水印，也可以保持关闭，然后继续进入照片管理。
        </div>
      )}
      {hasSelections && (
        <div data-testid="photo-settings-advisory" style={{
          padding: '12px 36px',
          background: C.warningBg, color: C.warningFg,
          borderBottom: `1px solid ${C.warningDot}`,
          fontSize: 12.5, lineHeight: 1.55,
        }}>
          <strong>已有客户提交选图。</strong>
          你仍可调整水印和客户标签，但修改前请确认现有选图记录是否仍能正确理解。
        </div>
      )}
      {/* Tab bar */}
      <div style={{
        display: 'flex', gap: 2, padding: '0 36px',
        borderBottom: `1px solid ${C.hair}`,
        background: '#fff', flexShrink: 0,
      }}>
        {tabs.map(t => {
          const on = tab === t.id;
          return (
            <button
              data-testid={`order-setting-tab-${t.id}`}
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: '14px 22px 12px',
                border: 'none', background: 'none',
                fontFamily: C.sans, textAlign: 'left',
                cursor: 'pointer',
                borderBottom: `3px solid ${on ? C.brand : 'transparent'}`,
                marginBottom: -1,
                transition: 'border-color .15s',
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: on ? 600 : 500, color: on ? C.ink : C.ink2 }}>
                <span style={{ color: on ? C.brand : C.ink3, display: 'inline-flex' }}><TabIcon name={t.id}/></span>
                {t.label}
              </div>
              <div data-testid={`order-setting-tab-status-${t.id}`} style={{ marginTop: 3, paddingLeft: 26, fontSize: 12, color: t.warn ? C.brand2 : C.ink3, fontWeight: t.warn ? 500 : 400 }}>
                {t.status}
              </div>
            </button>
          );
        })}
      </div>

      {showGlobalSettingHint && (
        <div
          data-testid={`global-setting-hint-${tab}`}
          style={{
            padding: '10px 36px',
            background: C.paper2,
            borderBottom: `1px solid ${C.hair}`,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexShrink: 0,
          }}
        >
          <span style={{ width: 7, height: 7, borderRadius: 4, background: C.brand, flexShrink: 0 }}/>
          <span style={{ flex: 1, fontSize: 12.5, color: C.ink3, lineHeight: 1.55 }}>
            {tab === 'watermark'
              ? '不想每个订单重复配置？可以在全局水印设置中预设，新建订单会自动带入，当前订单仍可单独调整。'
              : '常用标签可以在全局标签设置中统一维护，设置一次后，每个订单都可以直接选择。'}
          </span>
          <button
            type="button"
            data-testid={`global-setting-link-${tab}`}
            onClick={() => navigate(tab === 'watermark' ? '/user/setting/watermark' : '/user/setting/tag')}
            style={{
              padding: '6px 11px',
              borderRadius: 7,
              border: `1px solid ${C.hair2}`,
              background: '#fff',
              color: C.ink2,
              fontFamily: C.sans,
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {tab === 'watermark' ? '设置全局水印' : '管理全局标签'}
          </button>
        </div>
      )}

      {/* Tab content */}
      {tab === 'tags' && (
        <TagPanel tags={tags} availableTags={availableTags} onSave={handleTagsSave} onCreate={handleTagCreate} />
      )}
      {tab === 'resolution' && (
        <PreviewResolutionPanel
          scope="order"
          value={ordersStore.currentOrder?.previewMaxDimension || ordersStore.currentOrder?.effectivePreviewMaxDimension}
          legacy={!ordersStore.currentOrder?.previewMaxDimension}
          capability={userStore.previewResolution}
          onSave={value => ordersStore.updateOrder(orderId, { previewMaxDimension: value })
            .then(() => ordersStore.fetchOrder(orderId))
            .then(() => snackbarStore.showSnackbar('本订单清晰度已保存'))
            .catch(error => { snackbarStore.showSnackbar('保存失败，请重试', 'error'); throw error; })}
          onLocked={() => { snackbarStore.showSnackbar('升级到高级会员可使用超清', 'info'); chargeStore.openChargeDialog(); }}
        />
      )}
      {tab === 'watermark' && (
        <WatermarkEditor
          wm={wm}
          onChange={setWmSync}
          onAutoSave={handleWmSave}
          showActions
          compact
          settingScope="order"
        />
      )}
    </StepShell>
  );
}));

export default withAuth(Step2);
